/**
 * Centralized Meta Pixel + CAPI Event Helpers
 *
 * Every helper:
 *  1. Generates a unique event_id for browser ↔ server deduplication
 *  2. Fires the browser pixel event with that event_id
 *  3. Sends the same event server-side via the CAPI edge function
 *
 * Both sides are consent-aware and silently no-op when tracking is not initialised.
 *
 * ─── EVENT MAP ───────────────────────────────────────────────────────────────
 * Standard Meta events:
 *   PageView               → SPA route changes (AnalyticsProvider)
 *   ViewContent             → Pricing page viewed
 *   CompleteRegistration    → Account created
 *   InitiateCheckout        → Checkout started (with plan_type + billing_interval)
 *   Purchase                → Stripe-confirmed payment (server-verified)
 *
 * Custom PrepFrame events:
 *   OnboardingCompleted     → Onboarding flow finished
 *   RecommendedPlanShown    → Quiz recommendation displayed
 *   PremiumCTAClicked       → Premium CTA clicked
 *   FoundingMemberCTAClicked→ Founding Member CTA clicked
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { trackStandardEvent, trackCustomEvent, trackPageView as rawPageView } from './metaPixel';
import { generateEventId } from './config';
import { sendServerEvent } from './capi';
import { debugLogEvent } from './debug';
import { trackPinterestEvent } from './pinterest';
import { sendPinterestServerEvent } from './pinterestCapi';
import i18n from 'i18next';

// ── Shared types ─────────────────────────────────────────────────────────────

export type PlanType = 'premium' | 'founding_member';
export type BillingInterval = 'monthly' | 'yearly' | 'one_time';
export type SourceContext = 'pricing_page' | 'onboarding_end' | 'upgrade_sheet' | 'dashboard' | 'demo';

// ── Language enrichment ──────────────────────────────────────────────────────

function withLang(params?: Record<string, unknown>): Record<string, unknown> {
  return { ...params, content_language: i18n.language?.split('-')[0] || 'en' };
}

// ── Internal helpers ─────────────────────────────────────────────────────────

function dualStandard(eventName: string, params?: Record<string, unknown>, userData?: Record<string, unknown>) {
  const eid = generateEventId();
  const enriched = withLang(params);
  debugLogEvent(eventName, eid, 'both', enriched);
  trackStandardEvent(eventName, enriched, eid);
  sendServerEvent({ event_name: eventName, event_id: eid, custom_data: enriched, ...(userData ? { user_data: userData } : {}) });
}

function dualCustom(eventName: string, params?: Record<string, unknown>, userData?: Record<string, unknown>) {
  const eid = generateEventId();
  const enriched = withLang(params);
  debugLogEvent(eventName, eid, 'both', enriched);
  trackCustomEvent(eventName, enriched, eid);
  sendServerEvent({ event_name: eventName, event_id: eid, custom_data: enriched, user_data: userData });
}

function pinterestDual(
  eventName: string,
  params?: Record<string, unknown>,
  userData?: { em?: string },
) {
  const eid = generateEventId();
  trackPinterestEvent(eventName, params, eid);
  sendPinterestServerEvent({ event_name: eventName, event_id: eid, user_data: userData, custom_data: params });
}

// ─── Standard Meta Events ────────────────────────────────────────────────────

/** SPA page view – wired via AnalyticsProvider */
export function trackPageView() {
  const eid = generateEventId();
  const langData = withLang();
  debugLogEvent('PageView', eid, 'both', langData);
  rawPageView(eid);
  sendServerEvent({ event_name: 'PageView', event_id: eid, custom_data: langData });
  pinterestDual('page_visit');
}

/** Pricing page viewed */
export function trackViewContent(params: { content_name: string; content_category?: string }) {
  dualStandard('ViewContent', params);
}

/** Successful account creation */
export function trackCompleteRegistration(params?: { signup_method?: string; email?: string }) {
  const { email, ...eventParams } = params ?? {};
  const userData = email ? { em: email } : undefined;
  dualStandard('CompleteRegistration', eventParams, userData);
  pinterestDual('signup', { signup_method: params?.signup_method }, email ? { em: email } : undefined);
}

/** Checkout started — always includes plan_type and billing_interval */
export function trackInitiateCheckout(params: {
  plan_type: PlanType;
  billing_interval: BillingInterval;
  value: number;
  currency?: string;
  source_context?: SourceContext;
}) {
  dualStandard('InitiateCheckout', {
    content_name: params.plan_type,
    value: params.value,
    currency: params.currency ?? 'EUR',
    plan_type: params.plan_type,
    billing_interval: params.billing_interval,
    source_context: params.source_context,
  });
  pinterestDual('lead', { value: params.value, currency: params.currency ?? 'EUR', plan_type: params.plan_type });
}

/**
 * Purchase confirmed (Stripe-verified).
 *
 * When eventId is provided the server has already sent the CAPI event,
 * so only the browser pixel fires for deduplication.
 */
export function trackPurchase(
  params: {
    plan_type: PlanType;
    billing_interval: BillingInterval;
    value: number;
    currency?: string;
  },
  eventId?: string,
) {
  const data = {
    content_name: params.plan_type,
    value: params.value,
    currency: params.currency ?? 'EUR',
    plan_type: params.plan_type,
    billing_interval: params.billing_interval,
  };
  if (eventId) {
    // Stripe webhook already sent the CAPI event — only fire browser pixels
    trackStandardEvent('Purchase', data, eventId);
    trackPinterestEvent('checkout', { value: params.value, currency: params.currency ?? 'EUR', order_quantity: 1 }, eventId);
  } else {
    dualStandard('Purchase', data);
    pinterestDual('checkout', { value: params.value, currency: params.currency ?? 'EUR', order_quantity: 1 });
  }
}

// ─── Custom PrepFrame Events ─────────────────────────────────────────────────

/** Onboarding flow started – enriched with available user matching params */
export function trackOnboardingStarted(params?: {
  email?: string;
  phone?: string;
  external_id?: string;
  fb_login_id?: string;
  fn?: string;
  ln?: string;
  zip?: string;
  ct?: string;
  st?: string;
  dob?: string;
}) {
  // Pass PII as user_data (fbp/fbc are now auto-collected in capi.ts)
  const userData: Record<string, unknown> = { ...(params ?? {}) };
  Object.keys(userData).forEach((k) => userData[k] === undefined && delete userData[k]);
  dualCustom('OnboardingStarted', undefined, userData);
}

/** Individual quiz question answered */
export function trackOnboardingQuestionAnswered(params: {
  question_number: number;
  question_key: string;
  answer: string;
}) {
  dualCustom('OnboardingQuestionAnswered', params);
}

/** All quiz questions completed */
export function trackOnboardingQuestionsCompleted(params: {
  recommended_plan: string;
}) {
  dualCustom('OnboardingQuestionsCompleted', params);
}

/** Plan selected during onboarding */
export function trackPlanSelected(params: {
  plan: string;
  was_recommended: boolean;
}) {
  dualCustom('PlanSelected', params);
}

/** Onboarding flow completed (branding saved or skipped, quiz done, plan chosen) */
export function trackOnboardingCompleted() {
  dualCustom('OnboardingCompleted');
}

/** Quiz recommendation shown to user */
export function trackRecommendedPlanShown(params: {
  recommendation_result: string;
}) {
  dualCustom('RecommendedPlanShown', params);
}

/** Premium CTA clicked */
export function trackPremiumCTAClicked(params: {
  billing_interval: 'monthly' | 'yearly';
  source_context: SourceContext;
}) {
  dualCustom('PremiumCTAClicked', {
    plan_type: 'premium' as const,
    billing_interval: params.billing_interval,
    source_context: params.source_context,
  });
}

/** Founding Member CTA clicked */
export function trackFoundingMemberCTAClicked(params: {
  source_context: SourceContext;
}) {
  dualCustom('FoundingMemberCTAClicked', {
    plan_type: 'founding_member' as const,
    billing_interval: 'one_time' as const,
    source_context: params.source_context,
  });
}

// ─── Quick Tour Events ───────────────────────────────────────────────────────

/** Quick tour started */
export function trackQuickTourStarted() {
  dualCustom('QuickTourStarted');
}

/** Individual tour step viewed */
export function trackQuickTourStepViewed(params: {
  step_id: string;
  step_index: number;
}) {
  dualCustom('QuickTourStepViewed', params);
}

/** Quick tour completed */
export function trackQuickTourCompleted(params: { action: string }) {
  dualCustom('QuickTourCompleted', params);
}

/** Quick tour skipped */
export function trackQuickTourSkipped(params: { last_step_index: number }) {
  dualCustom('QuickTourSkipped', params);
}

// ─── Setup Tour Events ──────────────────────────────────────────────────────

/** Setup tour started */
export function trackSetupTourStarted() {
  dualCustom('SetupTourStarted');
}

/** Individual setup tour step viewed */
export function trackSetupTourStepViewed(params: {
  step_id: string;
  step_index: number;
}) {
  dualCustom('SetupTourStepViewed', params);
}

/** Setup tour completed */
export function trackSetupTourCompleted() {
  dualCustom('SetupTourCompleted');
}

/** Setup tour skipped */
export function trackSetupTourSkipped(params: { last_step_index: number }) {
  dualCustom('SetupTourSkipped', params);
}

// ─── Onboarding Flow Tracking ────────────────────────────────────────────────

/** Onboarding step entered — fires on every step transition */
export function trackOnboardingStepEntered(params: {
  step: number;
  step_name: string;
  time_since_last_step_ms?: number;
}) {
  dualCustom('OnboardingStepEntered', params);
}

/** Onboarding abandoned — use navigator.sendBeacon for reliability on tab close */
export function trackOnboardingAbandoned(params: {
  step: number;
  step_name: string;
  time_on_step_ms: number;
  total_elapsed_ms: number;
}) {
  const payload = {
    event_name: 'OnboardingAbandoned',
    step: params.step,
    step_name: params.step_name,
    time_on_step_ms: params.time_on_step_ms,
    total_elapsed_ms: params.total_elapsed_ms,
  };
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    import('./config').then(({ CAPI_ENDPOINT, META_PIXEL_ID }) => {
      if (!CAPI_ENDPOINT) return;
      const body = JSON.stringify({
        pixel_id: META_PIXEL_ID,
        events: [{
          event_name: 'OnboardingAbandoned',
          event_id: `${Date.now()}-abandoned`,
          event_time: Math.floor(Date.now() / 1000),
          action_source: 'website',
          custom_data: payload,
        }],
      });
      navigator.sendBeacon(CAPI_ENDPOINT, new Blob([body], { type: 'application/json' }));
    });
  }
  dualCustom('OnboardingAbandoned', params);
}
